// The store (24.14): signals plus named actions. Nothing else in the app is stateful.
import { setAutoFreeze } from 'immer';

// Committed sim states are deep-frozen in dev to catch accidental mutation (ADR-0002); production
// builds skip freezing for speed (the Section 2 budget).
setAutoFreeze((import.meta as { env?: { DEV?: boolean } }).env?.DEV ?? true);

export {
  draft,
  game,
  initialUi,
  patchUi,
  saves,
  settings,
  status,
  ui,
  type SeasonStatus,
  type Overlay,
  type Tab,
  type UiError,
  type UiState,
} from './state';
export { configureStorage, memoryStorage } from './storage';
export { withBoundary, APP_VERSION, diagnosticsOf, type Diagnostics } from './boundary';
export { requireGame, requireStatus } from './guards';
export {
  answerPrompt,
  continueGame,
  dismissError,
  dismissReport,
  endSeason,
  hasAutosave,
  newGame,
  toTitle,
} from './actions/game';
export { assignJob, cancelBuild, queueBuild, selectPerson, setPolicy } from './actions/decision';
// ---- Stream A (M2a/M2b rationing and the shortage dialog).
export {
  answerShortage,
  applySavedOrder,
  deleteSavedOrder,
  effectiveOverrides,
  effectivePolicy,
  effectiveSavedOrders,
  pinRation,
  reorderPriority,
  savePriorityOrder,
  setAnnounce,
  setAskOnShortage,
  setFoodFloor,
  setHouseholdSharing,
  setMyHousehold,
  setRationPolicy,
  setSeedReserve,
  type RationPolicyOptions,
  type RationedResource,
} from './actions/rations';
// ---- End stream A.
// ---- Stream B (M3a buildings, tools, housing).
export {
  moveBuild,
  pauseBuild,
  queueRepair,
  setHousingPolicy,
  setToolAllocation,
  setToolOverride,
  unqueueBuild,
} from './actions/build';
// ---- End stream B.
// ---- Stream E1 (M4a crime and justice).
export { answerJustice, setGuardMandate, setJusticePolicy } from './actions/justice';
// ---- End stream E1.
// ---- Stream C (M3b health).
export { setHealthCarePolicy, setTriageOverride } from './actions/health';
// ---- End stream C.
// ---- Stream D (M3c, M4c education, family and society).
export {
  answerPairing,
  setApprenticeship,
  setBurial,
  setChildLabour,
  setFestivalPolicy,
  setPairingPolicy,
  setRetirementAge,
  setSchoolSubjects,
  setSchooling,
  setWorkIntensity,
} from './actions/society';
// ---- End stream D.
// ---- Stream E2 (M4b, M4d migration and events).
export { answerEvent, setMigrationPolicy } from './actions/events';
// ---- End stream E2.
// ---- Stream F (M5a shell, M5c persistence).
export type {
  AutoFillProposal,
  ListFilter,
  Notice,
  NoticeCode,
  OlderBalance,
  PeopleGrouping,
  PeopleSortKey,
  PeopleView,
} from './types';
export {
  flushProjection,
  projection,
  PROJECTION_DEBOUNCE_MS,
  PROJECTION_HORIZON,
  type ProjectionState,
} from './projection';
export { cfgOption, currentRunId, runConfig } from './run';
/** Persistence types the UI renders (the UI never imports persist directly, 24.2). */
export type { ExportFile, Motion, SaveMeta, Settings, TextSize, Theme } from '../persist';
export { AUTOSAVE_MAX, AUTOSAVE_MIN } from '../persist';
export {
  applyAutoFill,
  discardAutoFill,
  editDraft,
  proposeAutoFill,
  renamePerson,
} from './actions/decision';
export {
  cancelEndSeason,
  closeOverlay,
  dismissNotice,
  hoverPerson,
  openOverlay,
  openPersonCard,
  openPrompt,
  openSaves,
  requestEndSeason,
  setListFilter,
  setPeopleGrouping,
  setPeopleSort,
  setTab,
  toggleGroup,
} from './actions/shell';
export { initStore, setSettings } from './actions/settings';
export {
  continueOnSavedConfig,
  deleteSlot,
  exportRun,
  forkOntoCurrentConfig,
  importRun,
  importRunText,
  loadAutosave,
  loadSlot,
  saveToSlot,
} from './actions/persist';
export { observerPause, observerPlay, observerStep } from './actions/observer';
export { fastForwardToShortage, runStewardFor } from './actions/debug';
// ---- End stream F.
// ---- Stream G (M5b the Ledger).
export { continueSandbox } from './actions/ledger';
// ---- End stream G.
